import { createContext, useContext, useEffect, ReactNode } from "react";
import { useState } from "react";
import themeConfig from "../configs/themeConfig";

interface CustomiseContextType {
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
  contentWidth: "full" | "boxed";
  setContentWidth: (contentWidth: "full" | "boxed") => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (sidebarCollapsed: boolean) => void;
  sidebarCollapseButton: boolean;
  setSidebarCollapseButton: (sidebarCollapseButton: boolean) => void;
  layout: "VerticalLayout" | "HorizontalLayout" | "FullLayout";
  setLayout: (layout: "VerticalLayout" | "HorizontalLayout" | "FullLayout") => void;
  navigationFull: boolean;
  setNavigationFull: (navigationFull: boolean) => void;
  navigationBg: boolean;
  setNavigationBg: (navigationBg: boolean) => void;
  direction: "ltr" | "rtl";
  setDirection: (direction: "ltr" | "rtl") => void;
  language: "fr" | "en";
  setLanguage: (language: "fr" | "en") => void;
}

const defaultContextValue: CustomiseContextType = {
  theme: themeConfig.theme as "light" | "dark",
  setTheme: () => { /* no-op */ },
  contentWidth: themeConfig.contentWidth as "full" | "boxed",
  setContentWidth: () => { /* no-op */ },
  sidebarCollapsed: themeConfig.sidebarCollapsed,
  setSidebarCollapsed: () => { /* no-op */ },
  sidebarCollapseButton: themeConfig.sidebarCollapseButton,
  setSidebarCollapseButton: () => { /* no-op */ },
  layout: themeConfig.layout as "VerticalLayout" | "HorizontalLayout" | "FullLayout",
  setLayout: () => { /* no-op */ },
  navigationFull: themeConfig.navigationFull,
  setNavigationFull: () => { /* no-op */ },
  navigationBg: themeConfig.navigationBg,
  setNavigationBg: () => { /* no-op */ },
  direction: themeConfig.direction as "ltr" | "rtl",
  setDirection: () => { /* no-op */ },
  language: themeConfig.language as "fr" | "en",
  setLanguage: () => { /* no-op */ },
};

export const CustomiseContext = createContext<CustomiseContextType>(defaultContextValue);

interface CustomiseContextProviderProps {
  children: ReactNode;
}

export function CustomiseContextProvider({ children }: CustomiseContextProviderProps) {
  const [theme, setTheme] = useState<"light" | "dark">(themeConfig.theme as "light" | "dark")
  const [contentWidth, setContentWidth] = useState<"full" | "boxed">(themeConfig.contentWidth as "full" | "boxed")
  const [sidebarCollapsed, setSidebarCollapsed] = useState(themeConfig.sidebarCollapsed)
  const [sidebarCollapseButton, setSidebarCollapseButton] = useState(themeConfig.sidebarCollapseButton)
  const [layout, setLayout] = useState<"VerticalLayout" | "HorizontalLayout" | "FullLayout">(themeConfig.layout as "VerticalLayout" | "HorizontalLayout" | "FullLayout")
  const [navigationFull, setNavigationFull] = useState(themeConfig.navigationFull)
  const [navigationBg, setNavigationBg] = useState(themeConfig.navigationBg)
  const [direction, setDirection] = useState<"ltr" | "rtl">(themeConfig.direction as "ltr" | "rtl")
  const [language, setLanguage] = useState<"fr" | "en">(themeConfig.language as "fr" | "en")

  // Update the sidebarCollapsed state with any local storage value
  useEffect(() => {
    const localCollapsed = localStorage.getItem("leedpulse-sidebarCollapsed") === "true" ? true : false;
    if (localCollapsed !== null) {
      // Synchro avec un systeme externe au rendu (DOM, storage, timer, toast,
      // navigation) : l'etat accompagne un effet de bord qui ne peut pas remonter au rendu.
      // eslint-disable-next-line react/set-state-in-effect
      setSidebarCollapsed(localCollapsed);
    }
  }, [sidebarCollapsed]);

  return (
    <CustomiseContext.Provider
      value={{ theme, setTheme, contentWidth, setContentWidth, sidebarCollapsed, setSidebarCollapsed, sidebarCollapseButton, setSidebarCollapseButton, layout, setLayout, navigationFull, setNavigationFull, navigationBg, setNavigationBg, direction, setDirection, language, setLanguage }}
    >
      {children}
    </CustomiseContext.Provider>
  );
}

export const useCustomise = (): CustomiseContextType => {
  const context = useContext(CustomiseContext);
  if (!context) {
    throw new Error("useCustomise s'utilise avec CustomiseContext.Provider");
  }

  return context;
};
